/**
 * CL-CART-01 — for each cart row: can it be bought, and if not, exactly why.
 *
 * Pure functions. No React, no network, so every case is cheap to test.
 *
 * Six answers rather than two, because D-13 requires the specific reason: a buyer who is
 * told "unavailable" goes looking for the wrong problem. D-12 keeps the row visible and
 * greyed out, excluded from totals, with checkout blocked until it is dealt with — so
 * nobody discovers a lost item after entering card details.
 *
 * The shipping half is NOT reimplemented here. `resolveShippingFee` in shippingRules.js
 * already resolves the zone and the fee, is shared with the Express server, and its own
 * comments draw the line this module depends on: "ask the buyer" (no address yet) versus
 * "the listing data is broken". Duplicating that logic is how a mirror drifts.
 */

import {
  getShippingConfig,
  resolveShippingFee,
  FEE_PRICED,
  FEE_BLOCKED,
  FEE_NOT_PRICED,
  FEE_AWAITING_DESTINATION,
  FEE_NOT_OFFERED,
} from './shippingRules';
import { quantityLimitsFor } from './cart';
// The existing single answer to "is this listing's delivery set up?" — the publish gate the
// edit wizard uses. The cart asks the same question and must not grow a second opinion.
import { isDeliveryTabComplete } from './shipping';

export const AVAILABLE = 'available';
export const SOLD_OUT = 'sold-out';
export const NOT_FOR_SALE = 'not-for-sale';
/**
 * "We have not read this listing yet" — which is NOT the same as "it is gone", and was
 * reported as gone. A missing listing is the state of every row before the fetch resolves
 * and of every row after it FAILS, so the first paint of the page told the buyer their whole
 * cart was unavailable, and a single network blip made that permanent: "No longer available"
 * on every row, with the panel urging them to remove the items to continue.
 *
 * It still blocks checkout — nothing may be bought on data we have not verified — but it is
 * not counted as unavailable and carries no reason, because there is nothing to report yet.
 */
export const PENDING = 'pending';
export const INSUFFICIENT_STOCK = 'insufficient-stock';
export const NOT_SHIPPABLE = 'not-shippable';
export const SHIPPING_INCOMPLETE = 'shipping-incomplete';

const listingTypeConfigOf = (listing, listingTypes = []) => {
  const listingType = listing?.attributes?.publicData?.listingType;
  return listingTypes.find(t => t.listingType === listingType);
};

const stockTypeOf = (listing, listingTypes = []) =>
  listingTypeConfigOf(listing, listingTypes)?.stockType;

/**
 * One row's verdict.
 *
 * The order of the checks is the point. A closed listing with no stock and broken shipping
 * reports NOT_FOR_SALE, because that is the fact the buyer can act on — telling them
 * "cannot ship to your country" about a listing that no longer exists sends them hunting
 * for a delivery problem that does not exist.
 */
export const annotateCartItem = (cartItem, listing, options = {}) => {
  // `listingsResolved` defaults to true so a caller that knows nothing about loading states
  // keeps the old meaning: a missing listing is a missing listing. Only the page, which
  // knows whether its fetch has succeeded, passes false.
  const { destinationCountry = null, listingTypes = [], listingsResolved = true } = options;

  const publicData = listing?.attributes?.publicData;
  const currentStock = listing?.currentStock?.attributes?.quantity;
  const limits = quantityLimitsFor(stockTypeOf(listing, listingTypes), currentStock);

  const shipping = listing
    ? resolveShippingFee({
        publicData,
        destinationCountry,
        quantity: cartItem?.quantity || 1,
      })
    : { status: FEE_NOT_OFFERED, amountInSubunits: null, zone: null };

  const status = (() => {
    // 0. Do we know anything about it yet? Before the listings arrive — and after the fetch
    //    fails — there is no listing here, and that is an absence of knowledge, not a
    //    verdict about the item.
    if (!listing) return listingsResolved ? NOT_FOR_SALE : PENDING;

    // 1. Is it still for sale at all?
    if (listing.attributes?.state !== 'published') return NOT_FOR_SALE;

    // 2. Is there any stock?
    if (!limits.hasStock) return SOLD_OUT;

    // 3. Enough stock for what the buyer asked for? The quantity is never changed here —
    //    AC-13 requires the buyer to be told and to confirm.
    if (limits.maxQuantity != null && (cartItem?.quantity || 1) > limits.maxQuantity) {
      return INSUFFICIENT_STOCK;
    }

    // 4. Shipping. FEE_AWAITING_DESTINATION is deliberately NOT a problem: with no
    //    delivery address there is no zone, so no price can exist yet. That is AC-18's
    //    "calculated at checkout". Treating it as an error would block every buyer who has
    //    not typed an address, which is all of them at first.
    if (shipping.status === FEE_BLOCKED) return NOT_SHIPPABLE;

    // FEE_NOT_OFFERED means `shippingEnabled: false`, which is NOT the same as broken. For a
    // pickup-only listing it is the complete and intended state: CL-SHIP-01 section C note 5
    // hides the shipping price fields entirely when a seller picks pickup only, and its
    // AC-18 covers exactly this listing. Two places already answered this the other way —
    // shippingLineItems.js returns no line item rather than an error, and
    // isDeliveryTabComplete lets the listing publish — so the cart calling it broken made
    // the row unsellable while the server that charges for it said yes.
    //
    // Delegated rather than re-derived from `pickupEnabled` here: one more copy of "is
    // delivery set up" is how the three answers drifted apart in the first place.
    if (shipping.status === FEE_NOT_OFFERED) {
      return isDeliveryTabComplete(publicData, listingTypeConfigOf(listing, listingTypes))
        ? AVAILABLE
        : SHIPPING_INCOMPLETE;
    }

    if (shipping.status === FEE_NOT_PRICED) {
      // Never fall through to zero: zero legitimately means free shipping (CL-SHIP-01 D9),
      // so "not set up" and "free" must not be confused. D-8 blocks instead.
      return SHIPPING_INCOMPLETE;
    }

    return AVAILABLE;
  })();

  // Money comes from the LIVE listing, never the cart's snapshot. The *Snapshot fields
  // exist only so a row can paint before the listing loads; a price the buyer is shown or
  // charged must be the current one. A listing that is gone has no price at all rather
  // than a stale one — a number nobody can honour is worse than a blank.
  const livePriceInSubunits = Number.isInteger(listing?.attributes?.price?.amount)
    ? listing.attributes.price.amount
    : null;
  const liveCurrency = listing?.attributes?.price?.currency || null;

  // A PENDING row has no live listing to price from, so it paints from its snapshot — which
  // is the one job the snapshots have ("so a row can paint before the listing loads"). This
  // fallback is for PENDING alone: a row whose listing is genuinely gone still gets no price
  // rather than a stale one, and an available row is always priced from the live listing.
  const isPending = status === PENDING;
  const unitPriceInSubunits =
    livePriceInSubunits ??
    (isPending && Number.isInteger(cartItem?.priceSnapshotInSubunits)
      ? cartItem.priceSnapshotInSubunits
      : null);
  const currency = liveCurrency || (isPending ? cartItem?.currencySnapshot || null : null);
  const quantity = cartItem?.quantity || 1;

  // E4: the price moved while the item sat in the cart. This is the ONE legitimate use of a
  // stored snapshot in money terms — not to charge it, but to notice that it is stale. The
  // buyer is charged `unitPriceInSubunits` either way; the note only says the figure changed
  // since they added it, which is what stops a silent increase going unnoticed.
  //
  // Compared only within one currency. A snapshot in another currency is not a price rise,
  // it is a different unit, and calling it a change would be a false alarm.
  // Against the LIVE price, never the fallback above: comparing a snapshot with itself can
  // only ever say "unchanged", and a row we have not read yet has no news about its price.
  const priceChanged =
    Number.isInteger(cartItem?.priceSnapshotInSubunits) &&
    Number.isInteger(livePriceInSubunits) &&
    !!liveCurrency &&
    cartItem.currencySnapshot === liveCurrency &&
    cartItem.priceSnapshotInSubunits !== livePriceInSubunits;

  // How long the seller takes to post it (AC-9's "shipping information", and the walkthrough's
  // "dispatch in 2 days"). resolveShippingFee reads the same config but returns only the fee,
  // so this is read directly rather than widening that function's contract. A listing from
  // before CL-SHIP-01 gets a synthesized config with no handling time, hence the null.
  const handlingTime = getShippingConfig(publicData)?.handlingTimeDays;
  const handlingTimeDays = Number.isInteger(handlingTime) ? handlingTime : null;

  // Whether this row gets a quantity control — decided per ROW, not per seller. One seller
  // can list a one-of-a-kind piece beside a multi-quantity one, so a single stockType for
  // the whole group hands one of those two rows the wrong control.
  //
  // A stepper that cannot move is the noise AC-9 rules out, so `multipleItems` with a stock
  // of one gets no control either. The exception is a row already above the ceiling: that
  // buyer has to be able to bring the quantity down, and removing the row is a different
  // offer from reducing it. A sold-out row is not that case — there is no quantity it could
  // be reduced to — which is what the hasStock guard is for.
  const isOverCeiling =
    limits.hasStock && limits.maxQuantity != null && quantity > limits.maxQuantity;

  // `hasStock` gates the normal branch because `maxQuantity: null` is ambiguous: it is "no
  // ceiling" for an endless listing and "we do not know" for a finite one whose currentStock
  // is missing. hasStock separates them — it is true for the infinite stock types whatever
  // the count, and false for a finite listing with none. Without it, the unknown case read
  // as endless and offered an unbounded stepper on a row already marked sold out.
  const showsQuantityControl =
    isOverCeiling ||
    (limits.hasStock &&
      limits.allowsMultiple &&
      (limits.maxQuantity == null || limits.maxQuantity > 1));

  return {
    ...cartItem,
    status,
    blocksCheckout: status !== AVAILABLE,
    availableStock: Number.isInteger(currentStock) ? currentStock : null,
    // The row's own cap and its own answer about the stepper, so no consumer has to
    // re-derive either from a stockType it would have to look up again.
    maxQuantity: limits.maxQuantity,
    showsQuantityControl,
    unitPriceInSubunits,
    currency,
    priceChanged,
    // The figure it moved FROM. "Price updated" alone tells a buyer something changed but
    // not whether to care; the old price is what decides whether they keep the item.
    previousPriceInSubunits: priceChanged ? cartItem.priceSnapshotInSubunits : null,
    handlingTimeDays,
    lineTotalInSubunits: unitPriceInSubunits == null ? null : unitPriceInSubunits * quantity,
    // Kept whole so the row can show the fee, or "calculated at checkout", without
    // resolving it a second time.
    shipping,
    // The live title, so a row cannot keep showing a stale snapshot after a rename.
    title: listing?.attributes?.title || cartItem?.titleSnapshot || null,
    isPriced: shipping.status === FEE_PRICED,
    // A fee is only FINAL once we know where it is going. A listing with no origin falls
    // back to its domestic price even with no destination (the legacy path in
    // resolveShippingFee) — correct for pricing, but AC-18 forbids showing a domestic
    // figure as final before an address exists. Without this, one row read "Shipping
    // $1.00" beside neighbours reading "calculated at checkout".
    isShippingFinal: shipping.status === FEE_PRICED && !!shipping.destinationCountry,
    // CL-NEG-01 D6/AC1 — whether THIS listing's seller allows offers at all, so a bundle of
    // several sellers' rules can be checked without a second lookup. `false` for a row with no
    // listing yet (PENDING): the caller must not count an unread row as offers-disabled, or the
    // bundle-offer action would flash "Buy It Now only" on every first paint.
    offersEnabled: publicData?.offersEnabled === true,
  };
};

/** Every row in one seller's group, plus what it means for the group as a whole. */
export const annotateSellerGroup = (group, listingsById = {}, options = {}) => {
  const items = (group?.items || []).map(item =>
    annotateCartItem(item, listingsById[item.listingId] || null, options)
  );

  const buyable = items.filter(i => !i.blocksCheckout);
  // Counted separately from the unavailable rows: a row we have not read yet blocks checkout
  // like any unverified row, but it is not a fault, and counting it as one is what put
  // "1 item unavailable — remove it to check out" on a cart whose listings were still
  // loading.
  const pendingCount = items.filter(i => i.status === PENDING).length;

  return {
    ...group,
    items,
    blocksCheckout: isCheckoutBlocked({ items }),
    // D-12: an unavailable row is excluded from totals, so it must not be counted in what
    // the buyer thinks they are paying for.
    buyableItemCount: buyable.reduce((n, i) => n + (i.quantity || 0), 0),
    pendingItemCount: pendingCount,
    unavailableItemCount: items.length - buyable.length - pendingCount,
    itemSubtotalInSubunits: buyable.reduce((n, i) => n + (i.lineTotalInSubunits || 0), 0),
  };
};

/**
 * The single gate. An empty group blocks too: there is nothing to pay for, and letting a
 * payment start on an empty basket is worse than a disabled button.
 */
export const isCheckoutBlocked = annotatedGroup => {
  const items = annotatedGroup?.items || [];
  return items.length === 0 || items.some(i => i.blocksCheckout);
};
